/**
 * Centralized Token Manager
 *
 * Provides a singleton for managing auth tokens across the application.
 * Handles automatic token refresh on 401 errors and prevents concurrent refresh attempts.
 */

import { authService } from './authService';

type TokenChangeListener = (token: string | null) => void;
type LogoutListener = () => void;

class TokenManager {
  private refreshPromise: Promise<string | null> | null = null;
  private tokenChangeListeners: Set<TokenChangeListener> = new Set();
  private logoutListeners: Set<LogoutListener> = new Set();
  private isRefreshing = false;

  /**
   * Get the current access token
   */
  getToken(): string | null {
    const stored = authService.getStoredTokens();
    return stored?.accessToken || null;
  }

  /**
   * Get the current refresh token
   */
  getRefreshToken(): string | null {
    const stored = authService.getStoredTokens();
    return stored?.refreshToken || null;
  }

  /**
   * Check if we have valid tokens
   */
  hasValidTokens(): boolean {
    const stored = authService.getStoredTokens();
    return !!stored?.accessToken && !!stored?.refreshToken;
  }

  /**
   * Check if token is expired or about to expire
   */
  isTokenExpired(bufferSeconds: number = 60): boolean {
    return authService.isTokenExpired(bufferSeconds);
  }

  /**
   * Refresh the access token
   * Prevents concurrent refresh attempts - all callers wait for the same promise
   */
  async refreshToken(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (this.refreshPromise) {
      console.log('[TokenManager] Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) {
      console.log('[TokenManager] No refresh token available');
      this.notifyLogout();
      return null;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh(stored.refreshToken);

    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
      this.isRefreshing = false;
    }
  }

  private async doRefresh(refreshToken: string): Promise<string | null> {
    try {
      console.log('[TokenManager] Refreshing access token...');
      const tokens = await authService.refreshToken(refreshToken);
      console.log('[TokenManager] Token refreshed successfully');
      this.notifyTokenChange(tokens.accessToken);
      return tokens.accessToken;
    } catch (error) {
      console.error('[TokenManager] Token refresh failed:', error);
      this.notifyLogout();
      return null;
    }
  }

  /**
   * Handle a 401 error - attempt to refresh and retry
   * Returns the new token if refresh succeeded, null if failed
   */
  async handleUnauthorized(): Promise<string | null> {
    console.log('[TokenManager] Handling 401 unauthorized...');
    return this.refreshToken();
  }

  /**
   * Register a listener for token changes
   */
  onTokenChange(listener: TokenChangeListener): () => void {
    this.tokenChangeListeners.add(listener);
    return () => this.tokenChangeListeners.delete(listener);
  }

  /**
   * Register a listener for logout events
   */
  onLogout(listener: LogoutListener): () => void {
    this.logoutListeners.add(listener);
    return () => this.logoutListeners.delete(listener);
  }

  private notifyTokenChange(token: string | null): void {
    this.tokenChangeListeners.forEach(listener => {
      try {
        listener(token);
      } catch (e) {
        console.error('[TokenManager] Token change listener error:', e);
      }
    });
  }

  private notifyLogout(): void {
    authService.clearTokens();
    this.logoutListeners.forEach(listener => {
      try {
        listener();
      } catch (e) {
        console.error('[TokenManager] Logout listener error:', e);
      }
    });
  }

  /**
   * Check if a refresh is currently in progress
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

// Singleton instance
export const tokenManager = new TokenManager();
